import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { useThemeStore } from '../store/themeStore';
import {
  FlagIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  HomeIcon,
  AcademicCapIcon,
  HeartIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';

interface Goal {
  id: string;
  user_id: string;
  name: string;
  goal_type: string;
  target_amount: number;
  current_amount: number;
  target_date: string;
  priority: string;
  status: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface Contribution {
  id: string;
  goal_id: string;
  amount: number;
  date: string;
  notes?: string;
  created_at: string;
}

interface Milestone {
  id: string;
  goal_id: string;
  name: string;
  target_percentage: number;
  reached: boolean;
  reached_date?: string;
}

interface GoalStatus {
  goal_id: string;
  name: string;
  progress_percentage: number;
  days_remaining: number;
  on_track: boolean;
  required_monthly_contribution: number;
  projected_completion_date?: string;
  milestones: Milestone[];
}

interface GoalType {
  id: string;
  name: string;
  description: string;
  suggested_timeline_months: number;
  icon: string;
}

interface Recommendation {
  goal_name: string;
  recommendation: string;
  priority: string;
}

const Goals: React.FC = () => {
  const { t } = useTranslation('goals');
  const theme = useThemeStore((state) => state.theme);

  // Determine if we're in dark mode for date input styling
  const isDarkMode = theme === 'dark' ||
    (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const dateInputStyle: React.CSSProperties = { colorScheme: isDarkMode ? 'dark' : 'light' };

  // State
  const [activeTab, setActiveTab] = useState<'overview' | 'goals' | 'contributions'>('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [goals, setGoals] = useState<Goal[]>([]);
  const [goalStatuses, setGoalStatuses] = useState<GoalStatus[]>([]);
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [summary, setSummary] = useState<any>(null);

  // Reference data
  const [goalTypes, setGoalTypes] = useState<GoalType[]>([]);

  // Modal state
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showContributionModal, setShowContributionModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);

  // Form state
  const [goalForm, setGoalForm] = useState({
    name: '',
    goal_type: 'emergency_fund',
    target_amount: 0,
    current_amount: 0,
    target_date: '',
    priority: 'medium',
    notes: '',
  });

  const [contributionForm, setContributionForm] = useState({
    goal_id: '',
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  // Load data on mount
  useEffect(() => {
    loadGoalTypes();
    loadGoals();
    loadSummary();
    loadRecommendations();
  }, []);

  useEffect(() => {
    if (goals.length > 0) {
      loadGoalStatuses();
      loadContributions();
    }
  }, [goals]);

  const loadGoalTypes = async () => {
    try {
      const response = await apiClient.get('/goals/types');
      setGoalTypes(response.data.goal_types || []);
    } catch (err) {
      console.error('Failed to load goal types:', err);
    }
  };

  const loadGoals = async () => {
    try {
      const response = await apiClient.get('/goals/');
      setGoals(response.data.goals || []);
    } catch (err) {
      console.error('Failed to load goals:', err);
    }
  };

  const loadGoalStatuses = async () => {
    try {
      const statuses = await Promise.all(
        goals.map(async goal => {
          const response = await apiClient.get(`/goals/${goal.id}/status`);
          return response.data;
        })
      );
      setGoalStatuses(statuses);
    } catch (err) {
      console.error('Failed to load goal statuses:', err);
    }
  };

  const loadContributions = async () => {
    try {
      const allContributions: Contribution[] = [];
      for (const goal of goals) {
        const response = await apiClient.get(`/goals/${goal.id}/contributions`);
        allContributions.push(...(response.data.contributions || []));
      }
      setContributions(allContributions.sort((a, b) =>
        new Date(b.date).getTime() - new Date(a.date).getTime()
      ));
    } catch (err) {
      console.error('Failed to load contributions:', err);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await apiClient.get('/goals/summary');
      setSummary(response.data);
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  };

  const loadRecommendations = async () => {
    try {
      const response = await apiClient.get('/goals/recommendations');
      setRecommendations(response.data.recommendations || []);
    } catch (err) {
      console.error('Failed to load recommendations:', err);
    }
  };

  const handleSaveGoal = async () => {
    setLoading(true);
    setError(null);
    try {
      if (editingGoal) {
        await apiClient.put(`/goals/${editingGoal.id}`, goalForm);
      } else {
        await apiClient.post('/goals/', goalForm);
      }
      await loadGoals();
      await loadSummary();
      setShowGoalModal(false);
      resetGoalForm();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGoal = async (id: string) => {
    if (!window.confirm(t('confirmDeleteGoal'))) return;
    try {
      await apiClient.delete(`/goals/${id}`);
      await loadGoals();
      await loadSummary();
    } catch (err) {
      console.error('Failed to delete goal:', err);
    }
  };

  const handleAddContribution = async () => {
    setLoading(true);
    setError(null);
    try {
      await apiClient.post(`/goals/${contributionForm.goal_id}/contributions`, {
        amount: contributionForm.amount,
        date: contributionForm.date,
        notes: contributionForm.notes,
      });
      await loadGoals();
      await loadSummary();
      setShowContributionModal(false);
      resetContributionForm();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.addFailed'));
    } finally {
      setLoading(false);
    }
  };

  const resetGoalForm = () => {
    setGoalForm({
      name: '',
      goal_type: 'emergency_fund',
      target_amount: 0,
      current_amount: 0,
      target_date: '',
      priority: 'medium',
      notes: '',
    });
    setEditingGoal(null);
  };

  const resetContributionForm = () => {
    setContributionForm({
      goal_id: '',
      amount: 0,
      date: new Date().toISOString().split('T')[0],
      notes: '',
    });
  };

  const openEditGoal = (goal: Goal) => {
    setEditingGoal(goal);
    setGoalForm({
      name: goal.name,
      goal_type: goal.goal_type,
      target_amount: goal.target_amount,
      current_amount: goal.current_amount,
      target_date: goal.target_date,
      priority: goal.priority,
      notes: goal.notes || '',
    });
    setShowGoalModal(true);
  };

  const openContributionModal = (goalId: string) => {
    setContributionForm({ ...contributionForm, goal_id: goalId });
    setShowContributionModal(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getGoalIcon = (goalType: string) => {
    const icons: Record<string, React.ReactNode> = {
      emergency_fund: <ShieldCheckIcon className="h-6 w-6" />,
      down_payment: <HomeIcon className="h-6 w-6" />,
      retirement: <BanknotesIcon className="h-6 w-6" />,
      debt_payoff: <CurrencyDollarIcon className="h-6 w-6" />,
      vacation: <GlobeAltIcon className="h-6 w-6" />,
      education: <AcademicCapIcon className="h-6 w-6" />,
      wedding: <HeartIcon className="h-6 w-6" />,
      car: <TrophyIcon className="h-6 w-6" />,
      investment: <ArrowTrendingUpIcon className="h-6 w-6" />,
      health: <HeartIcon className="h-6 w-6" />,
      home_improvement: <HomeIcon className="h-6 w-6" />,
      other: <FlagIcon className="h-6 w-6" />,
    };
    return icons[goalType] || icons.other;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate progress percentage for a goal
  // For debt payoff: progress = how much of original debt has been paid off
  // For savings goals: progress = how much saved toward target
  const calculateProgress = (goal: Goal): number => {
    if (goal.goal_type === 'debt_payoff') {
      // For debt payoff, we need to track original amount
      // Since current_amount decreases toward target_amount (usually 0),
      // we calculate based on how much has been paid relative to original debt
      // Original debt is stored in a separate field or calculated from contributions
      // For now: if current > target, progress is (original - current) / original * 100
      // We'll use a simple calculation: 100 - (current/original * 100) where original = current + contributions
      const totalContributions = contributions
        .filter(c => c.goal_id === goal.id)
        .reduce((sum, c) => sum + c.amount, 0);
      const originalDebt = goal.current_amount + totalContributions;
      if (originalDebt <= 0) return goal.current_amount <= goal.target_amount ? 100 : 0;
      const paidOff = originalDebt - goal.current_amount;
      return Math.min(100, (paidOff / originalDebt) * 100);
    } else {
      // For savings goals: progress toward target
      if (goal.target_amount <= 0) return goal.current_amount > 0 ? 100 : 0;
      return (goal.current_amount / goal.target_amount) * 100;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'on_track':
        return 'bg-blue-100 text-blue-800';
      case 'behind':
        return 'bg-yellow-100 text-yellow-800';
      case 'at_risk':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getGoalTypeName = (typeId: string) => {
    const type = goalTypes.find(t => t.id === typeId);
    return type?.name || typeId.replace(/_/g, ' ');
  };

  // Styles - using Tailwind dark: modifier
  const cardClass = 'rounded-xl shadow-lg p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700';

  const tabClass = (isActive: boolean) => `
    px-4 py-2 rounded-lg font-medium transition-colors
    ${isActive
      ? 'bg-blue-600 text-white'
      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
    }
  `;

  const inputClass = 'w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:[color-scheme:dark]';

  const selectClass = 'w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent';

  return (
    <div className="min-h-screen p-6 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <FlagIcon className="h-8 w-8 text-purple-500" />
              {t('title')}
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {t('subtitle')}
            </p>
          </div>
          <button
            onClick={() => {
              resetGoalForm();
              setShowGoalModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            {t('newGoal')}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5" />
            {error}
          </div>
        )}

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className={cardClass}>
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <FlagIcon className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className={`text-sm text-gray-600 dark:text-gray-400`}>
                    {t('activeGoals')}
                  </p>
                  <p className="text-xl font-bold">{summary.total_goals}</p>
                </div>
              </div>
            </div>

            <div className={cardClass}>
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className={`text-sm text-gray-600 dark:text-gray-400`}>
                    {t('totalTarget')}
                  </p>
                  <p className="text-xl font-bold">{formatCurrency(summary.total_target_amount)}</p>
                </div>
              </div>
            </div>

            <div className={cardClass}>
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <BanknotesIcon className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className={`text-sm text-gray-600 dark:text-gray-400`}>
                    {t('totalSaved')}
                  </p>
                  <p className="text-xl font-bold">{formatCurrency(summary.total_current_amount)}</p>
                </div>
              </div>
            </div>

            <div className={cardClass}>
              <div className="flex items-center gap-3">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <ChartBarIcon className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className={`text-sm text-gray-600 dark:text-gray-400`}>
                    {t('overallProgress')}
                  </p>
                  <p className="text-xl font-bold">{summary.overall_progress?.toFixed(1)}%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={tabClass(activeTab === 'overview')}
          >
            {t('tabs.overview')}
          </button>
          <button
            onClick={() => setActiveTab('goals')}
            className={tabClass(activeTab === 'goals')}
          >
            {t('tabs.goals')}
          </button>
          <button
            onClick={() => setActiveTab('contributions')}
            className={tabClass(activeTab === 'contributions')}
          >
            {t('tabs.contributions')}
          </button>
        </div>

        {/* Tab Content */}
        <div className={cardClass}>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div>
              {/* Goals Progress */}
              <h3 className="text-lg font-semibold mb-4">{t('goalProgress')}</h3>

              {goals.length === 0 ? (
                <div className="text-center py-12">
                  <FlagIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{t('noGoals')}</h3>
                  <p className={`mb-6 text-gray-600 dark:text-gray-400`}>
                    {t('noGoalsDescription')}
                  </p>
                  <button
                    onClick={() => setShowGoalModal(true)}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {t('createFirstGoal')}
                  </button>
                </div>
              ) : (
                <div className="space-y-4 mb-8">
                  {goals.map(goal => {
                    const progress = calculateProgress(goal);
                    const status = goalStatuses.find(s => s.goal_id === goal.id);

                    return (
                      <div
                        key={goal.id}
                        className={`p-4 rounded-lg ${
                          'bg-gray-50 dark:bg-gray-700'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${
                              'bg-white dark:bg-gray-600'
                            }`}>
                              {getGoalIcon(goal.goal_type)}
                            </div>
                            <div>
                              <h4 className="font-semibold">{goal.name}</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {getGoalTypeName(goal.goal_type)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs ${getPriorityColor(goal.priority)}`}>
                              {t(`priority.${goal.priority}`)}
                            </span>
                            {status && (
                              <span className={`px-2 py-1 rounded text-xs ${
                                status.on_track ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {status.on_track ? t('onTrack') : t('behind')}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="mb-2">
                          <div className="flex justify-between text-sm mb-1">
                            <span>{formatCurrency(goal.current_amount)}</span>
                            <span>{formatCurrency(goal.target_amount)}</span>
                          </div>
                          <div className="w-full bg-gray-300 rounded-full h-3">
                            <div
                              className={`h-3 rounded-full transition-all ${
                                progress >= 100
                                  ? 'bg-green-500'
                                  : progress >= 75
                                    ? 'bg-blue-500'
                                    : progress >= 50
                                      ? 'bg-yellow-500'
                                      : 'bg-orange-500'
                              }`}
                              style={{ width: `${Math.min(progress, 100)}%` }}
                            />
                          </div>
                        </div>

                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600 dark:text-gray-400">
                            {progress.toFixed(1)}% {t('complete')}
                          </span>
                          <div className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4" />
                            <span>{t('targetDate')}: {formatDate(goal.target_date)}</span>
                          </div>
                        </div>

                        {status && status.required_monthly_contribution > 0 && (
                          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            {t('monthlyNeeded')}: {formatCurrency(status.required_monthly_contribution)}
                          </div>
                        )}

                        <div className="flex justify-end gap-2 mt-3">
                          <button
                            onClick={() => openContributionModal(goal.id)}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                          >
                            {t('addContribution')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Recommendations */}
              {recommendations.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <SparklesIcon className="h-5 w-5 text-yellow-500" />
                    {t('recommendations')}
                  </h3>
                  <div className="space-y-3">
                    {recommendations.map((rec, idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg border-l-4 ${
                          rec.priority === 'high'
                            ? 'border-red-500 bg-red-50'
                            : rec.priority === 'medium'
                              ? 'border-yellow-500 bg-yellow-50'
                              : 'border-green-500 bg-green-50'
                        } dark:bg-opacity-10`}
                      >
                        <p className="font-medium text-gray-800 dark:text-gray-200">{rec.goal_name}</p>
                        <p className={`text-sm text-gray-600 dark:text-gray-400`}>
                          {rec.recommendation}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Goals Tab */}
          {activeTab === 'goals' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{t('allGoals')}</h3>
              </div>

              {goals.length === 0 ? (
                <div className="text-center py-8">
                  <FlagIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600 dark:text-gray-400">
                    {t('noGoals')}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={'border-gray-200 dark:border-gray-700'}>
                        <th className="text-left p-3">{t('goal')}</th>
                        <th className="text-left p-3">{t('type')}</th>
                        <th className="text-right p-3">{t('target')}</th>
                        <th className="text-right p-3">{t('current')}</th>
                        <th className="text-center p-3">{t('progress')}</th>
                        <th className="text-center p-3">{t('priority')}</th>
                        <th className="text-center p-3">{t('targetDate')}</th>
                        <th className="text-center p-3">{t('actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {goals.map(goal => {
                        const progress = calculateProgress(goal);

                        return (
                          <tr
                            key={goal.id}
                            className={`border-t ${
                              'border-gray-200 dark:border-gray-700'
                            }`}
                          >
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                {getGoalIcon(goal.goal_type)}
                                <span className="font-medium">{goal.name}</span>
                              </div>
                            </td>
                            <td className="p-3 capitalize">{getGoalTypeName(goal.goal_type)}</td>
                            <td className="p-3 text-right">{formatCurrency(goal.target_amount)}</td>
                            <td className="p-3 text-right">{formatCurrency(goal.current_amount)}</td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-300 rounded-full h-2">
                                  <div
                                    className="bg-blue-500 h-2 rounded-full"
                                    style={{ width: `${Math.min(progress, 100)}%` }}
                                  />
                                </div>
                                <span className="text-sm">{progress.toFixed(0)}%</span>
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-1 rounded text-xs ${getPriorityColor(goal.priority)}`}>
                                {t(`priority.${goal.priority}`)}
                              </span>
                            </td>
                            <td className="p-3 text-center">{formatDate(goal.target_date)}</td>
                            <td className="p-3">
                              <div className="flex justify-center gap-2">
                                <button
                                  onClick={() => openEditGoal(goal)}
                                  className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteGoal(goal.id)}
                                  className="p-1 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Contributions Tab */}
          {activeTab === 'contributions' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{t('recentContributions')}</h3>
                <button
                  onClick={() => {
                    if (goals.length > 0) {
                      setContributionForm({ ...contributionForm, goal_id: goals[0].id });
                      setShowContributionModal(true);
                    }
                  }}
                  disabled={goals.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <PlusIcon className="h-5 w-5" />
                  {t('addContribution')}
                </button>
              </div>

              {contributions.length === 0 ? (
                <div className="text-center py-8">
                  <BanknotesIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600 dark:text-gray-400">
                    {t('noContributions')}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={'border-gray-200 dark:border-gray-700'}>
                        <th className="text-left p-3">{t('date')}</th>
                        <th className="text-left p-3">{t('goal')}</th>
                        <th className="text-right p-3">{t('amount')}</th>
                        <th className="text-left p-3">{t('notes')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contributions.map(contribution => {
                        const goal = goals.find(g => g.id === contribution.goal_id);
                        return (
                          <tr
                            key={contribution.id}
                            className={`border-t ${
                              'border-gray-200 dark:border-gray-700'
                            }`}
                          >
                            <td className="p-3">{formatDate(contribution.date)}</td>
                            <td className="p-3">{goal?.name || '-'}</td>
                            <td className="p-3 text-right font-medium text-green-600">
                              +{formatCurrency(contribution.amount)}
                            </td>
                            <td className="p-3 text-sm text-gray-500 dark:text-gray-400">{contribution.notes || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <div className={`mt-6 p-4 rounded-lg ${'bg-yellow-50 dark:bg-gray-800'}`}>
          <p className={`text-sm ${'text-yellow-800 dark:text-gray-400'}`}>
            <strong>{t('disclaimer.title')}:</strong> {t('disclaimer.text')}
          </p>
        </div>

        {/* Goal Modal */}
        {showGoalModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`w-full max-w-lg rounded-xl shadow-xl ${
              'bg-white dark:bg-gray-800'
            }`}>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  {editingGoal ? t('editGoal') : t('newGoal')}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('goalName')}</label>
                    <input
                      type="text"
                      value={goalForm.name}
                      onChange={e => setGoalForm({ ...goalForm, name: e.target.value })}
                      placeholder={t('goalNamePlaceholder')}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('goalType')}</label>
                    <select
                      value={goalForm.goal_type}
                      onChange={e => setGoalForm({ ...goalForm, goal_type: e.target.value })}
                      className={selectClass}
                    >
                      {goalTypes.map(type => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('targetAmount')}</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={goalForm.target_amount}
                        onChange={e => setGoalForm({ ...goalForm, target_amount: parseFloat(e.target.value) || 0 })}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('currentAmount')}</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={goalForm.current_amount}
                        onChange={e => setGoalForm({ ...goalForm, current_amount: parseFloat(e.target.value) || 0 })}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('targetDate')}</label>
                      <input
                        type="date"
                        value={goalForm.target_date}
                        onChange={e => setGoalForm({ ...goalForm, target_date: e.target.value })}
                        className={inputClass}
                        style={dateInputStyle}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('priority')}</label>
                      <select
                        value={goalForm.priority}
                        onChange={e => setGoalForm({ ...goalForm, priority: e.target.value })}
                        className={selectClass}
                      >
                        <option value="high">{t('priority.high')}</option>
                        <option value="medium">{t('priority.medium')}</option>
                        <option value="low">{t('priority.low')}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('notes')}</label>
                    <textarea
                      value={goalForm.notes}
                      onChange={e => setGoalForm({ ...goalForm, notes: e.target.value })}
                      placeholder={t('notesPlaceholder')}
                      rows={3}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowGoalModal(false);
                      resetGoalForm();
                    }}
                    className={`px-4 py-2 rounded-lg ${
                      'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleSaveGoal}
                    disabled={loading || !goalForm.name || goalForm.target_amount < 0 || !goalForm.target_date}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? t('saving') : t('save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contribution Modal */}
        {showContributionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`w-full max-w-md rounded-xl shadow-xl ${
              'bg-white dark:bg-gray-800'
            }`}>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-4">{t('addContribution')}</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('selectGoal')}</label>
                    <select
                      value={contributionForm.goal_id}
                      onChange={e => setContributionForm({ ...contributionForm, goal_id: e.target.value })}
                      className={selectClass}
                    >
                      {goals.map(goal => (
                        <option key={goal.id} value={goal.id}>{goal.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('amount')}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={contributionForm.amount}
                      onChange={e => setContributionForm({ ...contributionForm, amount: parseFloat(e.target.value) || 0 })}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('date')}</label>
                    <input
                      type="date"
                      value={contributionForm.date}
                      onChange={e => setContributionForm({ ...contributionForm, date: e.target.value })}
                      className={inputClass}
                      style={dateInputStyle}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('notes')}</label>
                    <input
                      type="text"
                      value={contributionForm.notes}
                      onChange={e => setContributionForm({ ...contributionForm, notes: e.target.value })}
                      placeholder={t('contributionNotesPlaceholder')}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowContributionModal(false);
                      resetContributionForm();
                    }}
                    className={`px-4 py-2 rounded-lg ${
                      'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleAddContribution}
                    disabled={loading || !contributionForm.goal_id || contributionForm.amount <= 0}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? t('adding') : t('add')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Goals;
